import { injectable, inject } from 'inversify';
import type { ICasePersistent, ICaseState } from './CasePersistance/CasePersistenceHub';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from './Views.service';
import { OrientationPresetKind } from './interfaces';
import { OrientationPreset } from './models/OrientationPreset';

interface viewData {
  viewDescriptor: string;
  orientationPresets: Array<presetData>;
}

interface presetData {
  descriptor: string;
  label: string;
}

export type StateMetadata = Array<viewData>;

@injectable()
export class OrientationPresetsResolver implements ICasePersistent {
  private readonly _viewsService: ViewsService;

  public constructor(@inject(TYPES.ViewsService) viewsService: ViewsService) {
    this._viewsService = viewsService;
  }

  public saveState(): Promise<ICaseState> {
    const views = this._viewsService.views;
    const dataToSave: StateMetadata = [];

    views.forEach((view) => {
      const viewDataToSave: viewData = {
        viewDescriptor: view.getDescriptor(),
        orientationPresets: []
      };

      const customPresets = view.getOrientationPresets().filter((preset) => {
        return preset.kind === OrientationPresetKind.Custom;
      });
      customPresets?.forEach((preset) => {
        viewDataToSave.orientationPresets.push({
          descriptor: preset.descriptor,
          label: preset.label
        });
      });
      dataToSave.push(viewDataToSave);
    });

    const state: ICaseState<StateMetadata> = {
      version: '1.0',
      metadata: dataToSave
    };

    return Promise.resolve(state);
  }

  public loadState(metadata: unknown): Promise<void> {
    const viewsOrientationPresetsData = metadata as StateMetadata;

    viewsOrientationPresetsData.forEach((viewData) => {
      const view = this._viewsService.getViewByDescriptor(viewData.viewDescriptor);
      if (!view) return;

      viewData.orientationPresets.forEach((presetsData) => {
        view.addOrientationPreset(
          new OrientationPreset(presetsData.label, presetsData.descriptor, OrientationPresetKind.Custom)
        );
      });
    });
    return Promise.resolve();
  }
}
